import { useContext } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { apiGet, apiPost, apiPatch, apiDelete } from 'api';
import AuthContext from 'context/auth-context';

const getTableColumns = async (token) => {
  const url = 'admin/table_columns';
  const data = await apiGet({ url, token });
  return data;
};

const getTableColumn = async (token, id) => {
  const url = `admin/table_columns/${id}`;
  const data = await apiGet({ url, token });
  return data;
};

const createTableColumn = async (token, params) => {
  const url = 'admin/table_columns';
  const data = await apiPost({ url, token, objectName: 'table_column', params });
  return data;
};

const updateTableColumn = async (token, id, params) => {
  const url = `admin/table_columns/${id}`;
  const data = await apiPatch({ url, token, objectName: 'table_column', params });
  return data;
};

const deleteTableColumn = async (token, id) => {
  const url = `admin/table_columns/${id}`;
  const data = await apiDelete({ url, token });
  return data;
};

export function useAdminTableColumns({ options } = {}) {
  const token = useContext(AuthContext);
  const queryFn = () => getTableColumns(token);
  const queryKey = 'admin-table-columns';
  return useQuery(queryKey, queryFn, { ...options });
}

export function useAdminTableColumn({ id, options } = {}) {
  const token = useContext(AuthContext);
  const queryFn = () => getTableColumn(token, id);
  const queryKey = ['admin-table-column', id];
  return useQuery(queryKey, queryFn, { enabled: !!id, ...options });
}

export function useCreateAdminTableColumn({ options } = {}) {
  const token = useContext(AuthContext);
  const queryClient = useQueryClient();

  return useMutation(
    (params) => createTableColumn(token, params),
    {
      ...options,
      onSuccess: (data) => {
        queryClient.invalidateQueries('admin-table-columns');
        if (options?.onSuccess) {
          options.onSuccess(data);
        }
      }
    }
  );
}

export function useUpdateAdminTableColumn({ id, options } = {}) {
  const token = useContext(AuthContext);
  const queryClient = useQueryClient();

  return useMutation(
    (params) => updateTableColumn(token, id, params),
    {
      ...options,
      onSuccess: (data) => {
        queryClient.invalidateQueries('admin-table-columns');
        queryClient.invalidateQueries(['admin-table-column', id]);
        if (options?.onSuccess) {
          options.onSuccess(data);
        }
      }
    }
  );
}

export function useDeleteAdminTableColumn({ options } = {}) {
  const token = useContext(AuthContext);
  const queryClient = useQueryClient();

  return useMutation(
    (id) => deleteTableColumn(token, id),
    {
      ...options,
      onSuccess: (data) => {
        queryClient.invalidateQueries('admin-table-columns');
        if (options?.onSuccess) {
          options.onSuccess(data);
        }
      }
    }
  );
}
